import { useEffect, useRef } from "react";
import { useThemeSwitch, useMonacoThemeVariant } from "sj-ui-toolkit/src/theme";

// Theme file names must match files in public/monaco-themes/
const THEMES_BY_VARIANT = {
  default: { dark: "brilliance-dull", light: "chrome-devtools" },
  github: { dark: "github-dark", light: "github-light" },
};

const ALL_THEME_NAMES = Object.values(THEMES_BY_VARIANT).flatMap(pair => [pair.dark, pair.light]);

const resolveThemeName = (variant, appliedTheme) => {
  const mode = appliedTheme === "atlas-dark" ? "dark" : "light";
  const pair = THEMES_BY_VARIANT[variant] ?? THEMES_BY_VARIANT.default;
  return pair[mode];
};

const applyEditorBackgroundVar = themeJson => {
  const bg = themeJson?.colors?.["editor.background"];
  if (bg) {
    document.documentElement.style.setProperty("--monaco-editor-background", bg);
  }
};

export const useMonacoThemes = () => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const themesRef = useRef({});

  const { appliedTheme } = useThemeSwitch();
  const { variant } = useMonacoThemeVariant();
  const monacoTheme = resolveThemeName(variant, appliedTheme);

  const handleEditorMount = async (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Set aria-label for the editor's (IME) textarea to improve accessibility
    const textarea = editor.getDomNode()?.querySelector("textarea");
    if (textarea) {
      textarea.setAttribute("aria-label", "Code editor");
    }

    try {
      const responses = await Promise.all(
        ALL_THEME_NAMES.map(name => fetch(`/monaco-themes/${name}.json`))
      );
      const themes = await Promise.all(responses.map(r => r.json()));

      ALL_THEME_NAMES.forEach((name, i) => {
        monaco.editor.defineTheme(name, themes[i]);
        themesRef.current[name] = themes[i];
      });
      monaco.editor.setTheme(monacoTheme);
      applyEditorBackgroundVar(themesRef.current[monacoTheme]);
    } catch (error) {
      console.error("Failed to load Monaco themes:", error);
    }
  };

  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(monacoTheme);
      applyEditorBackgroundVar(themesRef.current[monacoTheme]);
    }
  }, [monacoTheme]);

  return {
    editorRef,
    monacoTheme,
    handleEditorMount,
  };
};
